import { Avatar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { QRModal } from "../modals/QRModal";
import { DeleteEmployee } from "./delete-employee";
import { EditEmployee } from "./edit-employee";
import { EmployeeUser } from "../../types";
import { ResendValidation } from "./resend-validation";
import { useState } from "react";
import { AvatarUpdateByManager } from "./avatar-update-by-manager";
import { InfoEmployee } from "./employee-info";
import { ToggleEmployeeOnLeave } from "./toggle-employee-on-leave";
import profileGenericPic from "../../assets/img/logos/profileGenericPic.jpg";
import { getBranchDisplayName } from "../../utils/branch-display-name";

interface EmployeeItemProps {
  employee: EmployeeUser;
  onUpdateEmployees: (updatedEmployees: EmployeeUser[]) => void;
  onOnLeaveChange: (userId: number, branchId: number, onLeave: boolean) => void;
}

export function EmployeeItem({
  employee,
  onUpdateEmployees,
  onOnLeaveChange,
}: EmployeeItemProps) {
  const [avatar, setAvatar] = useState(employee.imageUrl ?? "");
  const { t } = useTranslation();

  const validGroups = (employee.userGroups ?? []).filter(
    (group) => group.id != null && group.name
  );
  const branchLabel = getBranchDisplayName({
    fantasyName: employee.fantasyName,
    name: employee.branchName,
  });
  const emtpyGroup = validGroups.length === 0;

  const onAvatarChange = (imageUrl: string) => {
    setAvatar(imageUrl);
  };

  return (
    <div className="w-full font-light text-white justify-between items-center flex gap-4 bg-[#2d2f32] py-4 px-6 rounded-xl">
      {/* nombre */}
      <div className="flex h-full basis-1/4 shrink-0 min-w-0 items-center gap-6">
        <div className="text-2xl shrink-0 relative">
          <Avatar
            sx={{
              width: "4rem",
              height: "4rem",
            }}
            src={
              avatar ?? profileGenericPic
            }
            alt="Avatar Usuario"
          />
          <AvatarUpdateByManager
            onAvatarChange={onAvatarChange}
            employee={employee}
          />
        </div>
        <div
          title={employee.name}
          className="inline-flex w-fit min-w-0 items-center rounded-full border px-3 py-1 text-sm "
        >
          <span className="truncate">{employee.name}</span>
        </div>
      </div>

      {/* labels */}

      <div className="flex gap-4 basis-1/3 flex-grow min-w-0 items-center">
        {!employee.equalSplitTip && (
          <div
            className={`inline-flex w-fit min-w-0 overflow-hidden items-center rounded-full border px-3 py-1 text-sm max-w-[15ch]  ${
              emtpyGroup && "border-yellow text-yellow"
            }`}
          >
            {!emtpyGroup ? (
              <span
                className="truncate"
                title={validGroups.map((group) => group.name).join(", ")}
              >
                {validGroups.map((group) => group.name).join(", ")}
              </span>
            ) : (
              <span className="line-clamp-1">
                {t("branches.view.no-group")}
              </span>
            )}
          </div>
        )}

        {employee.equalSplitTip && (
          <div
            title={t("same-tip-label")}
            className="inline-flex w-fit max-w-[170px]  truncate items-center rounded-full border px-3 py-1 text-sm "
          >
            <span className="truncate">{t("same-tip-label")}</span>
          </div>
        )}
        <div
          title={branchLabel}
          className="inline-flex w-fit max-w-[170px] truncate items-center rounded-full border px-3 py-1 text-sm "
        >
          <span className="truncate">{branchLabel}</span>
        </div>
        {employee.onLeave && (
          <div
            title={t("employee.edit.leave")}
            className="inline-flex w-fit max-w-[170px] border-orange text-orange  truncate items-center rounded-full border px-3 py-1 text-sm "
          >
            <span className="truncate">{t("leave")}</span>
          </div>
        )}
        <div
          title={employee.qrDisplayDescription ?? t("employee.qr-config.label")}
          className="inline-flex w-fit max-w-[220px] truncate items-center rounded-full border px-3 py-1 text-sm"
        >
          <span className="truncate">
            {employee.qrDisplayShortName ?? t("employee.qr-config.label")}
          </span>
        </div>
        {!employee.isVerified && (
          <div className="inline-flex w-fit shrink-0 whitespace-nowrap items-center rounded-full border border-orange text-orange px-3 py-1 text-sm ">
            {t("pending-validation")}
          </div>
        )}
      </div>

      <div className="flex justify-end shrink-0 items-center gap-2">
        {/* employee config */}
        <div className="flex items-center gap-2 xl:gap-8">
          {employee.useQR && employee.isVerified && (
            <QRModal employee={employee} />
          )}
          {!employee.isVerified && <ResendValidation employee={employee} />}
          <InfoEmployee employee={employee} />
          <ToggleEmployeeOnLeave
            employee={employee}
            onOnLeaveChange={onOnLeaveChange}
          />
          <EditEmployee
            employee={employee}
            updateEmployees={onUpdateEmployees}
            onLeaveConfig={true}
          />
          <DeleteEmployee
            updateEmployees={onUpdateEmployees}
            userId={employee.userId}
            branchId={employee.branchId}
          />
        </div>
      </div>
    </div>
  );
}
